import { ThemeProvider } from "./ThemeProvider";
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';

import { LogIn } from "./pages/LogIn/LogIn";
import { SupportLogin } from "./pages/SupportLogin/SupportLogin";
import { SupportModeBanner } from "./components/SupportMode/SupportModeBanner";
import { SelectMember } from "./pages/SelectMember/SelectMember";
import { DashBoard } from "./pages/DashBoard/DashBoard";
import { DashboardSnapshot } from "./pages/DashBoard/DashboardSnapshot";
import { Funnel } from "./pages/Funnel/Funnel";
import { ReviewTests } from "./pages/Tests/ReviewTests";
import { ReviewingTest } from "./pages/Tests/ReviewingTest";
import { OpenTest } from "./pages/Tests/OpenTest";
import { Settings } from "./pages/Settings/Settings";
import { Logout } from "./pages/Logout/Logout";
import { ForgotPassword } from "./pages/ForgotPassword/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword/ResetPassword";
import { CreateTest } from "./pages/Tests/CreateTest";
import { ManageCampaigns } from "./pages/Campaigns/ManageCampaigns";
import { CreateCampaign } from "./pages/Campaigns/CreateCampaign";
import { CampaignReports } from "./pages/Campaigns/CampaignReports";
import { CreateReport } from "./pages/Campaigns/CreateReport";
import { AdLookup } from "./pages/Campaigns/AdLookup";
import { Report } from "./pages/Campaigns/Report";
import { AggregatedReports } from "./pages/Campaigns/AggregatedReports";
import { ManageHeatmaps } from "./pages/Insights/ManageHeatmaps";
import { HeatMapRecordingView } from "./pages/Insights/HeatMapRecordingView";
import { ScrollMapRecordingView } from "./pages/Insights/ScrollMapRecordingView";
import { CreateHeatmap } from "./pages/Insights/CreateHeatmap";
import { ManageScrollmaps } from "./pages/Insights/ManageScrollmaps";
import { CreateScrollmap } from "./pages/Insights/CreateScrollmap";
import { Recordings } from "./pages/Insights/Recordings";
import { RecordingsList } from "./pages/Insights/RecordingsList";
import { RecordingSetting } from "./pages/Insights/RecordingSetting";
import { RecordingView } from "./pages/Insights/RecordingView";
import { TestEditorVisual } from "./pages/Tests/TestEditorVisual";
import { TestEditorVisualmodern } from "./pages/Tests/TestEditorVisualmodern";
import { TestHistory } from "./pages/Tests/TestHistory";
import { TestHistoryReport } from "./pages/Tests/TestHistoryReport";
import { MultipleUserLogin } from "./components/MultipleUserLogin/MultipleUserLogin";
import { BillingDetails } from "./components/HeaderMenu/BillingDetails";
import { SystemUsage } from "./components/HeaderMenu/SystemUsage";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { visiUserSession } from './GeneralFunction';
import { visiUserToken } from './GeneralFunction';
import { useEffect, useState } from 'react';
import { NotFound  } from "./components/404NotFound/404NotFound";
import { AccountSetup  } from "./pages/LogIn/AccountSetup";
import { DemoReport  } from "./pages/DemoCampaign/demoReport";
import { Onboarding  } from "./pages/Onboarding/Onboarding";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";

const App = () => {
    return (
      <ThemeProvider>
        <AuthProvider>
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <SimpleBar autoHide={false} style={{ maxHeight: "100vh" }}>
              <Router>
                <SupportModeBanner />
                <Routes>
                    <Route path="/" element={<PreAuthCheck />} />
                    <Route path="/login" element={<PreAuthCheck />} />
                    <Route path="/support-login" element={<SupportLogin />} />
                    <Route path="/select-member" element={<SelectMember />} />
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                          <DashBoard />
                      </ProtectedRoute>
                    } />
                    {/* Public snapshot viewer — intentionally NOT wrapped in
                        ProtectedRoute. Recipients must be able to open the
                        shareable link without a Visiopt login. */}
                    <Route path="/dashboard/snapshot/:token" element={<DashboardSnapshot />} />
                    <Route path="/funnels" element={
                      <ProtectedRoute>
                          <Funnel />
                      </ProtectedRoute>
                    } />
                    <Route path="/review-tests" element={
                      <ProtectedRoute>
                          <ReviewTests />
                      </ProtectedRoute>
                    } />
                    <Route path="/reviewing-test" element={
                      <ProtectedRoute>
                          <ReviewingTest />
                      </ProtectedRoute>
                    } />
                    {/* Direct link from a notification button: opens the named test */}
                    <Route path="/open-test" element={
                      <ProtectedRoute>
                          <OpenTest />
                      </ProtectedRoute>
                    } />
                    <Route path="/test-history" element={
                      <ProtectedRoute>
                          <TestHistory />
                      </ProtectedRoute>
                    } />
                    <Route path="/test-history-report" element={
                      <ProtectedRoute>
                          <TestHistoryReport />
                      </ProtectedRoute>
                    } />
                    <Route path="/test-editor-visual/:pageid/:taguchi_type_id" element={
                      <ProtectedRoute>
                          <TestEditorVisual />
                      </ProtectedRoute>
                    } />
                    <Route path="/test-editor-visual-modern/:pageid/:taguchi_type_id" element={
                      <ProtectedRoute>
                          <TestEditorVisualmodern />
                      </ProtectedRoute>
                    } />
                    <Route path="/create-test" element={
                      <ProtectedRoute>
                          <CreateTest />
                      </ProtectedRoute>
                    } />
                    <Route path="/manage-campaigns" element={
                      <ProtectedRoute>
                          <ManageCampaigns />
                      </ProtectedRoute>
                    } />
                    <Route path="/create-campaign/:campaign_id" element={
                      <ProtectedRoute>
                          <CreateCampaign />
                      </ProtectedRoute>
                    } />
                    <Route path="/campaign-reports" element={
                      <ProtectedRoute>
                          <CampaignReports />
                      </ProtectedRoute>
                    } />
                    <Route path="/campaign-report/:reportId" element={
                      <ProtectedRoute>
                          <CreateReport />
                      </ProtectedRoute>
                    } />
                    <Route path="/ad-lookup" element={
                      <ProtectedRoute>
                          <AdLookup />
                      </ProtectedRoute>
                    } />
                    <Route path="/aggregated-reports" element={
                      <ProtectedRoute>
                          <AggregatedReports />
                      </ProtectedRoute>
                    } />
                    <Route path="/aggregated-report/:reportId" element={
                      <ProtectedRoute>
                          <Report />
                      </ProtectedRoute>
                    } />
                    <Route path="/heat-maps" element={
                      <ProtectedRoute>
                          <ManageHeatmaps />
                      </ProtectedRoute>
                    } />
                    <Route path="/heatmap-view/:HeatmapId" element={
                      <ProtectedRoute>
                          <HeatMapRecordingView />
                      </ProtectedRoute>
                    } />
                    <Route path="/create-heatmap" element={
                      <ProtectedRoute>
                          <CreateHeatmap />
                      </ProtectedRoute>
                    } />
                    <Route path="/scroll-maps" element={
                      <ProtectedRoute>
                          <ManageScrollmaps />
                      </ProtectedRoute>
                    } />
                    <Route path="/create-scrollmap" element={
                      <ProtectedRoute>
                          <CreateScrollmap />
                      </ProtectedRoute>
                    } />
                    <Route path="/scrollmap-view/:scrollmap_id" element={
                      <ProtectedRoute>
                          <ScrollMapRecordingView />
                      </ProtectedRoute>
                    } />
                    <Route path="/recordings" element={
                      <ProtectedRoute>
                          <Recordings />
                      </ProtectedRoute>
                    } />
                    <Route path="/recordings-list" element={
                      <ProtectedRoute>
                          <RecordingsList />
                      </ProtectedRoute>
                    } />
                    <Route path="/recording-setting" element={
                      <ProtectedRoute>
                          <RecordingSetting />
                      </ProtectedRoute>
                    } />
                    <Route path="/recording-view" element={
                      <ProtectedRoute>
                          <RecordingView />
                      </ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                      <ProtectedRoute>
                          <Settings />
                      </ProtectedRoute>
                    } />
                    <Route path="/logout" element={
                      <ProtectedRoute>
                          <Logout />
                      </ProtectedRoute>
                    } />
                    <Route path="/multiple-user-login" element={
                      <ProtectedRoute>
                          <MultipleUserLogin />
                      </ProtectedRoute>
                    } />
                    <Route path="/billing-details" element={
                      <ProtectedRoute>
                          <BillingDetails />
                      </ProtectedRoute>
                    } />
                    <Route path="/system-usage" element={
                      <ProtectedRoute>
                          <SystemUsage />
                      </ProtectedRoute>
                    } />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="*" element={<NotFound />} />
                    <Route path="/account_setup" element={<AccountSetup />} />
                    <Route path="/onboarding" element={
                      <ProtectedRoute>
                        <Onboarding />
                      </ProtectedRoute>
                    } />
                    <Route path="/demo_report" element={<DemoReport />} />
                </Routes>
              </Router>
            </SimpleBar>
          </GoogleOAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    );
};

function PreAuthCheck() {
  const navigate = useNavigate();
  const [shouldLogin, setShouldLogin] = useState(false);
  useEffect(() => {
    if(visiUserToken() && visiUserSession() && visiUserSession().page){
      navigate(visiUserSession().page);
    }else{
      setShouldLogin(true);
    }
  }, [navigate]);
  return shouldLogin ? <LogIn /> : null;
}

export default App;